import { useQuery } from '@tanstack/react-query';
import { Award, Wallet } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { MONTH_NAMES, formatDate, formatMoney, formatMonth } from '../lib/format';
import { Badge, Card, CardHeader, EmptyState, ErrorState, LoadingBlock, PageHeader, Table, Td, Th, Tr } from '../components/ui';
import { REWARD_TONE, rewardSizeKey } from './Rewards';

interface MyReward {
  id: string;
  days: number;
  status: 'GRANTED' | 'PAID' | 'CANCELLED';
  effectiveMonth: string;
  note: string | null;
  createdAt: string;
  paidAt: string | null;
  cancelledAt: string | null;
  paidWith: { year: number; month: number } | null;
}

/**
 * §36.4 — an employee may always see their own payslips.
 *
 * Payroll is run in sys now, but this is self-service and every employee has an
 * account here, so it stayed. It shows periods that have been paid or locked;
 * the API returns nothing but the signed-in person's own entries. Above them, their
 * rewards of a quarter or half of a day's pay (CR-004) — the reward notice opens
 * here: what each is, the word it came with, and whether it was paid yet.
 */
export function MyPayslipsPage() {
  const { t, locale } = useI18n();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['payroll', 'my-payslips'],
    queryFn: () => api.get<any[]>('/payroll/my-payslips'),
  });
  const rewards = useQuery({
    queryKey: ['rewards', 'mine'],
    queryFn: () => api.get<MyReward[]>('/rewards/mine'),
  });

  return (
    <>
      <PageHeader title={t('payroll.myPayslips')} count={data?.length} />

      {!!rewards.data?.length && (
        <Card className="mb-5" padded={false}>
          <CardHeader title={t('rewards.mineTitle')} className="p-5 pb-0" />
          <ul className="divide-y divide-[var(--line)]">
            {rewards.data.map((r) => (
              <li key={r.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 px-5 py-3">
                <div className="flex min-w-0 items-start gap-3">
                  <Award className="mt-0.5 h-4 w-4 shrink-0 text-ai-plasma-text dark:text-ai-plasma-lift" aria-hidden />
                  <div className="min-w-0">
                    <span className={r.status === 'CANCELLED' ? 'font-medium text-muted line-through' : 'font-medium text-[var(--text-primary)]'}>
                      {t(rewardSizeKey(r.days))}
                    </span>
                    <span className="ms-2 text-caption tabular text-muted">{formatDate(r.createdAt, locale)}</span>
                    {r.note && (
                      <p className="mt-0.5 text-body-sm text-[var(--text-secondary)]" dir="auto">
                        {r.note}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-end">
                  <Badge tone={REWARD_TONE[r.status]}>
                    {r.status === 'GRANTED' ? t('rewards.mineGranted') : t(`rewards.status.${r.status}` as TranslationKey)}
                  </Badge>
                  {r.paidWith && (
                    <span className="mt-0.5 block text-caption text-muted">
                      {t('rewards.paidWith', { month: formatMonth(r.paidWith.year, r.paidWith.month, locale) })}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.length ? (
          <EmptyState icon={<Wallet className="h-8 w-8" />} title={t('common.noResults')} hint="" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.month')}</Th>
                <Th align="end">{t('payroll.gross')}</Th>
                <Th align="end">{t('payroll.deductions')}</Th>
                <Th align="end">{t('payroll.net')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry) => (
                <Tr key={entry.id}>
                  <Td className="tabular">
                    {MONTH_NAMES[locale][entry.period.month - 1]} {entry.period.year}
                  </Td>
                  <Td align="end" className="tabular">{formatMoney(entry.grossAmount, entry.currency, locale)}</Td>
                  <Td align="end" className="tabular text-error-fg dark:text-error-dark">
                    −{formatMoney(entry.totalDeductions, entry.currency, locale)}
                  </Td>
                  <Td align="end" className="tabular font-semibold">
                    {formatMoney(entry.netAmount, entry.currency, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
